import clsx from 'clsx';
import React, {
  FunctionComponent,
  MouseEventHandler,
  ReactNode,
  useState,
} from 'react';

import Spinner from '../loading/spinner';
import ButtonDropdown from './button-dropdown';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'icon'
  | 'text'
  | 'underline';

export interface IDropDownItem {
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  onDisabledClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  label: string;
  icon?: string;
  divider?: boolean;
  auth: boolean;
  authText?: string;
}

interface IProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  variant?: ButtonVariant;
  title?: string;
  tabIndex?: number;
  style?: any;
  isDisabled?: boolean;
  onDisabledClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  dropDownItems?: IDropDownItem[];
  shouldStretch?: boolean;
  type?: 'button' | 'submit';
  isLoading?: boolean;
  hoverEffect?: boolean;
  positionMenuAbove?: boolean;
}

const getVariantStyle = (
  variant: ButtonVariant,
  shouldStretch = false,
  hoverEffect = true
) => {
  switch (variant) {
    case 'primary': {
      return clsx(
        'disabled:bg-primary disabled:opacity-50 disabled:cursor-not-allowed',
        ' mx-0 py-2 bg-primary active:bg-primary-active hover:bg-primary-hover',
        hoverEffect && 'hover:bg-primary-hover ',
        'items-center justify-center',
        shouldStretch ? 'flex-grow px-1' : 'px-4 flex-shrink-0'
      );
    }
    case 'secondary': {
      return clsx(
        'disabled:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed',
        'bg-secondary border-2 border-primary',
        'mx-0 py-2 active:bg-secondary-active active:border-primary-dark ',
        hoverEffect && 'hover:bg-secondary-hover',
        'items-center justify-center',
        shouldStretch ? 'flex-grow px-1' : 'px-4 flex-shrink-0'
      );
    }
    case 'icon': {
      return clsx(
        'item-center flex justify-center p-2 w-9 h-9 hover:bg-gray-10 rounded shadow-none'
      );
    }
    case 'text': {
      return clsx('active:bg-gray-10', hoverEffect && 'hover:bg-gray-10');
    }
    case 'underline': {
      return clsx(
        'disabled:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed',
        'px-0 bg-secondary border-2 border-l-0 border-r-0 border-t-0 border-primary rounded-none shadow-none',
        'mx-0 py-2 active:bg-secondary-active active:border-primary-dark',
        hoverEffect && 'hover:bg-secondary-hover'
      );
    }
    default: {
      return '';
    }
  }
};

/**
 * TODO: Need to refactor the dropdown button type & icon button type out to a different component
 * this component should have a lot less logic than it does right now.
 */
/** @deprecated  use BaseButton instead */
const Button: FunctionComponent<IProps> = ({
  onClick,
  children = null,
  className = '',
  dropDownItems = undefined,
  hoverEffect = true,
  isDisabled = false,
  isLoading = false,
  onDisabledClick = undefined,
  positionMenuAbove = false,
  shouldStretch = false,
  style = null,
  tabIndex = 0,
  title = '',
  type = 'button',
  variant = 'primary',
  ...other
}: IProps) => {
  const renderChildren = () => {
    if (isLoading) {
      return <Spinner />;
    }

    return children;
  };

  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const handleOnDropDownButtonClick = () =>
    setIsDropDownOpen((prevState) => !prevState);

  // This will return a drop down button
  if (dropDownItems?.length) {
    return (
      <ButtonDropdown
        dropDownItems={dropDownItems}
        isDropDownOpen={isDropDownOpen}
        positionMenuAbove={positionMenuAbove}
        setIsDropDownOpen={setIsDropDownOpen}
        type={type}
      >
        <button
          className={clsx(
            isDropDownOpen && 'bg-secondary-active',
            getVariantStyle(variant!, shouldStretch, hoverEffect),
            'z-70 mx-0 flex h-full w-full items-center justify-center px-1 py-2 text-xs font-semibold text-gray-100',
            ' rounded shadow transition-colors duration-300'
          )}
          disabled={isDisabled}
          style={style}
          tabIndex={tabIndex}
          title={title}
          type={type === 'button' ? 'button' : 'submit'}
          onClick={handleOnDropDownButtonClick}
        >
          {children}{' '}
        </button>
      </ButtonDropdown>
    );
  }

  // This will return a regular button
  return (
    <button
      className={clsx(
        className,
        getVariantStyle(variant!, shouldStretch, hoverEffect),
        variant && variant !== 'text' && 'shadow',
        shouldStretch ? 'flex-grow px-1' : 'flex-shrink-0 px-4',
        'flex flex-shrink text-gray-100',
        ' mx-0 whitespace-nowrap rounded py-2 text-xs font-semibold transition duration-300',
        isDisabled && onDisabledClick ? 'opacity-50' : ''
      )}
      disabled={isDisabled && !onDisabledClick}
      style={style}
      tabIndex={tabIndex}
      title={title}
      // To get pass button using static string
      type={type === 'button' ? 'button' : 'submit'}
      onClick={(e) => (isDisabled ? onDisabledClick?.(e) : onClick?.(e))}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
    >
      {renderChildren()}
    </button>
  );
};

export default Button;
